import { inject, Injectable } from '@angular/core';
import { Environment } from '@rma/dashboard/generic/util/environment';
import { FINANCE } from '@rma/generic/domain/types';
import { combineLatest, map, Observable } from 'rxjs';
import { COLLECT_GROUP_FEATURES, CollectGroupFeature, getCollectGroupItems } from '../entities/nav-items-collect';
import {
  getManageGroupItems,
  getSettingsGroupItems,
  MANAGE_GROUP_FEATURES,
  ManageGroupFeature,
  SettingGroupFeature,
  SETTINGS_GROUP_FEATURES,
} from '../entities/nav-items-manage';
import { getOverview, getProfile, NavigationUserType } from '../entities/nav-items-misc';
import { getMonitorGroupItems, MonitorGroupFeature, MONITORING_GROUP_FEATURES } from '../entities/nav-items-monitor';
import { filterShareItems, getShareGroupItems, SHARE_GROUP_FEATURES } from '../entities/nav-items-share';
import { createNavigationGroup, NavigationGooglePermissions, NavigationGroup } from '../entities/side-nav-items';
import { SideNavigationConfigService } from './side-navigation-config.service';

@Injectable({
  providedIn: 'root',
})
export class IndividualFinanceSideMenuService {
  private readonly config = inject(SideNavigationConfigService);
  private readonly environment = inject(Environment);

  public getNavigation(): Observable<NavigationGroup[]> {
    return combineLatest({
      googlePermissions: this.config.getGoogleConfig$,
      showCuratedSocial: this.config.showSocialStudioLink$,
      showSocialMediaManager: this.config.showSocialMediaManager$,
      showGoogle: this.config.showGoogle$,
      showBillingAndSubs: this.config.showBillingAndSubs$,
    }).pipe(
      map(({ googlePermissions, showCuratedSocial, showSocialMediaManager, showGoogle, showBillingAndSubs }) => {
        const { baseUrl } = this.environment;
        const userType: NavigationUserType = 'agent';
        return [
          createNavigationGroup([getProfile()]),
          createNavigationGroup([getOverview()]),
          this.getCollectGroup(userType),
          this.getShareGroup(userType, googlePermissions, baseUrl, showCuratedSocial, showSocialMediaManager, showGoogle),
          this.getMonitoringGroup(),
          this.getManageItems(baseUrl, false, showBillingAndSubs),
        ].filter((group) => group.items.length);
      }),
    );
  }

  private getCollectGroup(userType: NavigationUserType) {
    const notAvailable: CollectGroupFeature[] = ['leasing', 'nps', 'importReviews', 'properties'];
    const features = COLLECT_GROUP_FEATURES.filter((f) => !notAvailable.includes(f));

    const items = getCollectGroupItems(features, userType, FINANCE);
    return createNavigationGroup(items, 'collect');
  }

  private getShareGroup(
    agentType: NavigationUserType,
    googlePermissions: NavigationGooglePermissions,
    baseUrl: string,
    showStudio: boolean,
    showSocialMediaManager: boolean,
    showGoogle: boolean,
  ): NavigationGroup {
    const features = SHARE_GROUP_FEATURES.filter(
      filterShareItems(false, true, true, showStudio, showSocialMediaManager, false, showGoogle),
    );
    const items = getShareGroupItems(features, agentType, false, googlePermissions, baseUrl);
    return createNavigationGroup(items, 'share');
  }

  private getManageItems(baseUrl: string, enableDatafeeds: boolean, showBillingAndSubs: boolean): NavigationGroup {
    return createNavigationGroup(
      [
        ...getManageGroupItems(this.getManageGroupItems(showBillingAndSubs)),
        getSettingsGroupItems(this.getSettingsItems(enableDatafeeds), baseUrl),
      ],
      'manage',
    );
  }

  private getSettingsItems(enableDatafeeds: boolean): SettingGroupFeature[] {
    const notAvailable: SettingGroupFeature[] = ['manageTemplates', 'easyReviews', 'assistants'];

    if (!enableDatafeeds) {
      notAvailable.push('dataFeeds');
    }

    const items = SETTINGS_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));

    return items;
  }

  private getManageGroupItems(showBillingAndSubs: boolean) {
    const notAvailable: ManageGroupFeature[] = ['teamAndAdmins'];

    if (!showBillingAndSubs) {
      notAvailable.push('billingAndSubs');
    }

    const items = MANAGE_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));

    return items;
  }

  private getMonitoringGroup(): NavigationGroup {
    const notAvailble: MonitorGroupFeature[] = ['leaderboards'];
    const features = MONITORING_GROUP_FEATURES.filter((i) => !notAvailble.includes(i));
    return createNavigationGroup(getMonitorGroupItems(features), 'monitor');
  }
}
